import { useMemo } from "react";
import type { ReactNode } from "react";
import type { RangeOption } from "../../../constants/epicLeaderboard";
import { useApiResource } from "../../../hooks/useApiResource";
import type {
  PkFeedResponse,
  PkHeadToHead,
  PkInsights,
  PkMilestones
} from "../../../types/playerPk";
import { playerApiUrl } from "../../../utils/playerApi";
import ActivityHeatmap from "./ActivityHeatmap";
import ClansSection from "./ClansSection";
import HcSection from "./HcSection";
import HeadToHead from "./HeadToHead";
import MilestonesSection from "./MilestonesSection";
import MonthlyChart from "./MonthlyChart";
import OpponentsTable from "./OpponentsTable";
import PartnersLists from "./PartnersLists";
import PkSection, { PkEmpty, PkError } from "./PkSection";
import {
  CardSkeleton,
  TableSkeleton,
  TilesSkeleton,
  TwoCardsSkeleton
} from "./PkTabSkeletons";
import PkSummaryTiles from "./PkSummaryTiles";
import RecentFeed from "./RecentFeed";
import SoloAssistedSplit from "./SoloAssistedSplit";
import StreaksCard from "./StreaksCard";

type Props = {
  player: string;
  range: RangeOption;
  // ISO start of the range, undefined for all time.
  startDate: string | undefined;
  vs: string | null;
  feedPage: number;
  onVsChange: (opponent: string | null) => void;
  onFeedPageChange: (page: number) => void;
};

const PlayerPkTab = ({
  player,
  range,
  startDate,
  vs,
  feedPage,
  onVsChange,
  onFeedPageChange
}: Props) => {
  const scope = player.toLowerCase();

  // The three tab-wide requests start together when the tab opens.
  const insights = useApiResource<PkInsights>(
    useMemo(
      () => playerApiUrl(player, "pk-insights", { startDate }),
      [player, startDate]
    ),
    scope
  );
  const milestones = useApiResource<PkMilestones>(
    useMemo(() => playerApiUrl(player, "pk-milestones"), [player]),
    scope
  );
  const feed = useApiResource<PkFeedResponse>(
    useMemo(
      () =>
        playerApiUrl(player, "pk-feed", {
          startDate,
          page: feedPage > 1 ? feedPage : undefined
        }),
      [player, startDate, feedPage]
    ),
    scope
  );
  // Only when someone is picked.
  const headToHead = useApiResource<PkHeadToHead>(
    useMemo(
      () =>
        vs && vs.toLowerCase() !== scope
          ? playerApiUrl(player, "pk-head-to-head", { opponent: vs, startDate })
          : null,
      [player, scope, vs, startDate]
    ),
    scope
  );

  const handleCompare = (opponent: string) => {
    onVsChange(opponent);
    // Let the section re-render with the new opponent, then bring it into view.
    window.setTimeout(() => {
      document
        .getElementById("pk-head-to-head")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  if (insights.error?.status === 404) {
    return (
      <PkSection title="PK">
        <PkEmpty>No PK records found for {player}.</PkEmpty>
      </PkSection>
    );
  }

  const data = insights.data;
  const refetching = insights.loading && !!data;
  const noActivity =
    !!data &&
    data.summary.kills + data.summary.deaths + data.summary.assists === 0;

  // Insight-backed sections: skeleton while loading, nothing on error (one
  // error card sits at the top instead).
  const withInsights = (
    skeleton: ReactNode,
    render: (d: PkInsights) => ReactNode
  ): ReactNode => {
    if (data) return render(data);
    return insights.error ? null : skeleton;
  };

  return (
    <>
      {insights.error && !data && (
        <div className="mt-4">
          <PkError
            what="PK insights"
            error={insights.error}
            onRetry={insights.reload}
          />
        </div>
      )}

      {noActivity && (
        <div className="mt-4">
          <PkEmpty>No PK activity in this range.</PkEmpty>
        </div>
      )}

      {!noActivity && (
        <>
          <PkSection title="SUMMARY" refetching={refetching}>
            {withInsights(<TilesSkeleton />, (d) => (
              <PkSummaryTiles summary={d.summary} />
            ))}
          </PkSection>

          <PkSection title="OPPONENTS" refetching={refetching}>
            {withInsights(<TableSkeleton />, (d) => (
              <OpponentsTable
                opponents={d.opponents}
                range={range}
                onCompare={handleCompare}
              />
            ))}
          </PkSection>

          <HeadToHead
            player={player}
            range={range}
            startDate={startDate}
            vs={vs}
            result={headToHead}
            insights={insights}
            onVsChange={onVsChange}
          />

          <PkSection
            title="STREAKS"
            note="Kills and deaths in order; assists don't count."
            refetching={refetching}
          >
            {withInsights(<TwoCardsSkeleton height={190} />, (d) => (
              <StreaksCard inRange={d.streaks} allTime={milestones} />
            ))}
          </PkSection>

          <RecentFeed
            player={player}
            feed={feed}
            range={range}
            onPageChange={onFeedPageChange}
          />

          <PkSection title="ACTIVITY" refetching={refetching}>
            {withInsights(<CardSkeleton height={300} />, (d) => (
              <ActivityHeatmap activity={d.activity} />
            ))}
          </PkSection>

          <PkSection title="KILLS VS DEATHS BY MONTH" refetching={refetching}>
            {withInsights(<CardSkeleton height={320} />, (d) =>
              d.monthly.length === 0 ? (
                <PkEmpty>No monthly data in this range.</PkEmpty>
              ) : (
                <MonthlyChart monthly={d.monthly} />
              )
            )}
          </PkSection>

          <PkSection title="ASSISTS" refetching={refetching}>
            {withInsights(<TwoCardsSkeleton height={260} />, (d) => (
              <PartnersLists partners={d.partners} range={range} />
            ))}
          </PkSection>

          <PkSection title="SOLO VS ASSISTED KILLS" refetching={refetching}>
            {withInsights(<CardSkeleton height={110} />, (d) => (
              <SoloAssistedSplit summary={d.summary} />
            ))}
          </PkSection>

          {(!data || data.hc.kills > 0) && (
            <PkSection title="HARDCORE KILLS" refetching={refetching}>
              {withInsights(<CardSkeleton height={280} />, (d) => (
                <HcSection hc={d.hc} />
              ))}
            </PkSection>
          )}

          <PkSection title="CLANS" refetching={refetching}>
            {withInsights(<TwoCardsSkeleton height={260} />, (d) => (
              <ClansSection clans={d.clans} />
            ))}
          </PkSection>
        </>
      )}

      <PkSection
        title="MILESTONES"
        note="All time (not affected by the date range)"
      >
        {milestones.data ? (
          <MilestonesSection milestones={milestones.data} range={range} />
        ) : milestones.error ? (
          <PkError
            what="milestones"
            error={milestones.error}
            onRetry={milestones.reload}
          />
        ) : (
          <TilesSkeleton count={3} />
        )}
      </PkSection>
    </>
  );
};

export default PlayerPkTab;
